import { Component } from '@theme/component';

/**
 * WDW Tab-Slider: ARIA-Tablist mit Zähler und Zurück/Weiter-Pfeilen, kein Autoplay (Kundenstimmen, Cases-Seite).
 * Tabs: [data-slider-tab] mit aria-controls auf das Panel. Pfeiltasten in beide Richtungen, Pos1/Ende.
 * Die Wechsel-Animation folgt in Phase 9 über das Event „wdw:slide-change“.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [prev] - Zurück-Pfeil
 * @property {HTMLButtonElement} [next] - Weiter-Pfeil
 * @property {HTMLElement} [current] - aktuelle Nummer im Zähler
 *
 * @extends {Component<Refs>}
 */
class WdwTabSliderComponent extends Component {
  #startX = 0;
  #startY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#update(this.#index);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerup', this.#onPointerUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointerup', this.#onPointerUp);
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#startX = event.clientX;
    this.#startY = event.clientY;
  };

  /** Wischgeste (Touch): ab 40 px waagerecht wechseln, senkrechtes Scrollen bleibt frei (touch-action: pan-y) */
  #onPointerUp = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType !== 'touch') return;
    const dx = event.clientX - this.#startX;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(event.clientY - this.#startY)) return;
    this.#activate(this.#index + (dx < 0 ? 1 : -1), false);
  };

  /** @returns {HTMLButtonElement[]} */
  get tabs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-slider-tab]')]);
  }

  get #index() {
    return Math.max(
      0,
      this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );
  }

  /**
   * Tab per Klick aktivieren (on:click="/select").
   * @param {Event} event
   */
  select(event) {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[data-slider-tab]');
    if (tab instanceof HTMLButtonElement) this.#activate(this.tabs.indexOf(tab), false);
  }

  /** on:click="/previous" */
  previous() {
    this.#activate(this.#index - 1, false);
  }

  /** on:click="/next" */
  next() {
    this.#activate(this.#index + 1, false);
  }

  /**
   * Tastatursteuerung der Tablist (on:keydown="/onKeydown").
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    const count = this.tabs.length;
    const current = this.#index;
    let target = -1;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = (current + 1) % count;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = (current - 1 + count) % count;
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = count - 1;

    if (target < 0) return;
    event.preventDefault();
    this.#activate(target, true);
  }

  /**
   * @param {number} index
   * @param {boolean} focus
   */
  #activate(index, focus) {
    const tabs = this.tabs;
    const tab = tabs[index];
    if (!tab || index === this.#index) return;

    for (const other of tabs) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(other.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !selected;
    }
    if (focus) tab.focus();
    this.#reveal(tab);
    this.#update(index);
    this.dispatchEvent(new CustomEvent('wdw:slide-change', { detail: { tab, index }, bubbles: true }));
  }

  /**
   * Aktiven Tab sichtbar machen: in einer scrollbaren Tab-Leiste mittig, sonst wie bisher per scrollIntoView.
   * @param {HTMLElement} tab
   */
  #reveal(tab) {
    const list = tab.parentElement;
    const scrollable = list && (list.scrollHeight > list.clientHeight || list.scrollWidth > list.clientWidth);
    if (!list || !scrollable) {
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      return;
    }
    const listRect = list.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollBy({
      top: tabRect.top - listRect.top - (listRect.height - tabRect.height) / 2,
      left: tabRect.left - listRect.left - (listRect.width - tabRect.width) / 2,
      behavior: reduce ? 'auto' : 'smooth',
    });
  }

  /** @param {number} index */
  #update(index) {
    const { prev, next, current } = this.refs;
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= this.tabs.length - 1;
    if (current) current.textContent = String(index + 1);
  }
}

if (!customElements.get('wdw-tab-slider-component')) {
  customElements.define('wdw-tab-slider-component', WdwTabSliderComponent);
}
